import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    FaSearch, FaTrashAlt, FaPlus, FaUserPlus, FaTag, FaMoneyBillWave,
    FaExchangeAlt, FaCreditCard, FaCodeBranch, FaPause, FaPrint, FaBoxOpen
} from 'react-icons/fa';
import { Product, FamilyMember, Order } from '../../types';
import { fuzzySearch } from '../../utils/fuzzySearch';
import { nombreOperativo } from '../../utils/productName';
import { generateOrderPDF } from '../../services/pdfService';
import { useAuth } from '../../context/AuthContext';
import styles from './styles/PosScreen.module.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface PosScreenProps {
    /** Se dispara despues de cobrar, para que el shell pueda navegar a Ventas. */
    onSaleCompleted?: () => void;
}

type PosTab = 'ventas' | 'devoluciones' | 'cambios';

/**
 * Local unico del mostrador.
 *
 * El prototipo dejo de ser multi-sucursal: todas las ventas se imputan a este
 * local. No se muestra en pantalla (el operador no elige nada), pero viaja en
 * el payload para que el stock y los reportes sigan teniendo sucursal.
 *
 * El id NO se puede fijar en el codigo: lo asigna la base al crear el local
 * (ensure_erp_tables lo inserta con AUTO_INCREMENT, y en un deploy real no
 * arranca en 1). Se resuelve contra /locales; el nombre solo es el fallback
 * para elegir cual si hubiera varios.
 */
const LOCAL_UNICO = {
    nombre: 'Ardel',
    direccion: 'Av. San Martin 1199',
};

interface CartLine {
    /** Clave estable de la linea: permite repetir un mismo producto con distinta config. */
    key: string;
    product: Product;
    quantity: number;
}

/** Medios de pago del mostrador, en el orden en que aparecen en el ticket. */
const PAYMENT_METHODS: { value: string; label: string; icon: React.ReactNode }[] = [
    { value: 'Efectivo', label: 'Efectivo', icon: <FaMoneyBillWave /> },
    { value: 'Transferencia', label: 'Transferencia', icon: <FaExchangeAlt /> },
    { value: 'Debito', label: 'Débito', icon: <FaCreditCard /> },
    { value: 'Credito', label: 'Crédito', icon: <FaCreditCard /> },
];

/** Metodos validos para las lineas del pago mixto (incluye QR, que no tiene boton propio). */
const MIXED_PAYMENT_OPTIONS = ['Efectivo', 'Transferencia', 'QR', 'Debito', 'Credito'];

/**
 * Listas de precios del mostrador.
 *
 * El catalogo tiene un solo precio (el minorista) y el mayorista se deriva con
 * un factor fijo. Estos valores son un espejo de los que aplica el backend
 * (LISTAS_PRECIOS en app.py): sirven para mostrar el precio correcto mientras
 * se arma el ticket, pero el importe que queda guardado siempre lo recalcula el
 * servidor a partir del precio de la base.
 */
type PriceList = 'minorista' | 'mayorista';

const PRICE_LIST_DIFF_PCT = 21;

const PRICE_LISTS: { value: PriceList; label: string; factor: number }[] = [
    { value: 'minorista', label: 'Minorista', factor: 1 },
    { value: 'mayorista', label: 'Mayorista', factor: 1 - PRICE_LIST_DIFF_PCT / 100 },
];

const priceListFactor = (lista: PriceList) =>
    PRICE_LISTS.find(l => l.value === lista)?.factor ?? 1;

/** Precio unitario de un producto segun la lista activa. */
const precioDeLista = (precioBase: number, lista: PriceList) =>
    Math.round((Number(precioBase) || 0) * priceListFactor(lista) * 100) / 100;

const money = (n: number) => `$${Math.round(n).toLocaleString('es-AR')}`;

const todayISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const PosScreen: React.FC<PosScreenProps> = ({ onSaleCompleted }) => {
    const { user } = useAuth();

    const [tab, setTab] = useState<PosTab>('ventas');

    // Catalogo y stock del local. El mostrador opera siempre sobre el mismo
    // local (LOCAL_UNICO), asi que no hay selector ni estado de sucursal.
    const [products, setProducts] = useState<Product[]>([]);
    const [stockByProduct, setStockByProduct] = useState<Record<number, number>>({});
    /** Local del mostrador resuelto contra la base (null mientras carga). */
    const [localId, setLocalId] = useState<number | null>(null);

    // Lista de precios aplicada a la venta en curso.
    const [priceList, setPriceList] = useState<PriceList>('minorista');

    // Venta actual
    const [cart, setCart] = useState<CartLine[]>([]);
    const [customer, setCustomer] = useState<FamilyMember | null>(null);
    const [paymentMethod, setPaymentMethod] = useState('');
    const [manualDiscount, setManualDiscount] = useState(0);
    const [selectedPromo, setSelectedPromo] = useState('');
    const [coupon, setCoupon] = useState('');
    const [observations, setObservations] = useState('');

    // Pago mixto: se reparte el total entre varios metodos.
    const [isMixedPayment, setIsMixedPayment] = useState(false);
    const [mixedPayments, setMixedPayments] = useState<{ metodo: string; monto: number }[]>([
        { metodo: '', monto: 0 },
        { metodo: '', monto: 0 },
    ]);

    /*
     * Datos del cliente. Son SIEMPRE opcionales: la venta de mostrador se cobra
     * sin identificar a nadie. Se cargan desde el modal "Asignar cliente" y solo
     * sirven para poder facturar despues.
     */
    const [custDni, setCustDni] = useState('');
    const [custName, setCustName] = useState('');
    const [custPhone, setCustPhone] = useState('');
    const [custAddress, setCustAddress] = useState('');
    const [customerModalOpen, setCustomerModalOpen] = useState(false);

    // Ventas suspendidas: quedan en memoria hasta que se retoman o se cobra otra.
    const [suspended, setSuspended] = useState<CartLine[][]>([]);
    /** Ultima venta cobrada, para poder re-imprimir su comprobante. */
    const [lastOrder, setLastOrder] = useState<Order | null>(null);

    // Busqueda y filtros
    const [search, setSearch] = useState('');
    const [category, setCategory] = useState<string>('Todos');

    // UI
    const [loading, setLoading] = useState(false);
    const [toast, setToast] = useState<{ type: 'ok' | 'error'; text: string } | null>(null);
    /** Advertencia de stock antes de cobrar (reemplaza al confirm del navegador). */
    const [stockWarningOpen, setStockWarningOpen] = useState(false);
    const searchRef = useRef<HTMLInputElement>(null);

    const notify = useCallback((type: 'ok' | 'error', text: string) => {
        setToast({ type, text });
        window.setTimeout(() => setToast(null), 2200);
    }, []);

    /* ------------------------------------------------------------------ datos */

    useEffect(() => {
        fetch(`${API}/productos`)
            .then(res => res.json())
            .then(data => { if (Array.isArray(data)) setProducts(data); })
            .catch(err => console.error('Error cargando productos', err));
    }, []);

    // Local del mostrador. Se busca por nombre y, si no aparece, se usa el
    // primero: con un solo local esa es la respuesta correcta igual.
    useEffect(() => {
        const controller = new AbortController();
        fetch(`${API}/locales`, { signal: controller.signal })
            .then(res => (res.ok ? res.json() : Promise.reject(new Error('locales'))))
            .then((data: any[]) => {
                if (!Array.isArray(data) || data.length === 0) return;
                const propio = data.find(l => l.nombre_local === LOCAL_UNICO.nombre) ?? data[0];
                setLocalId(Number(propio.locales_id));
            })
            .catch(err => { if (!controller.signal.aborted) console.error('Error cargando locales', err); });

        return () => controller.abort();
    }, []);

    // Stock del local del mostrador: se usa solo para avisar, nunca bloquea la venta.
    useEffect(() => {
        if (localId === null) return;

        const controller = new AbortController();
        fetch(`${API}/admin/stock/report?id_local=${localId}`, { signal: controller.signal })
            .then(res => (res.ok ? res.json() : Promise.reject(new Error('stock'))))
            .then(raw => {
                const data = Array.isArray(raw) ? raw : (raw?.items ?? []);
                if (!Array.isArray(data)) return;
                // El backend devuelve los totales como string (vienen de SUM()),
                // asi que hay que convertirlos: '6' < 2 es false pero '6' seria
                // NaN en cualquier resta posterior.
                setStockByProduct(Object.fromEntries(
                    data.map((item: any) => [Number(item.idProductos), Number(item.stock_disponible ?? 0)])
                ));
            })
            .catch(err => { if (!controller.signal.aborted) console.error('Error cargando stock', err); });

        return () => controller.abort();
    }, [localId]);

    useEffect(() => {
        if (tab === 'ventas') searchRef.current?.focus();
    }, [tab]);

    /* --------------------------------------------------------------- catalogo */

    /** Chips de categoria: "Todos" + las categorias presentes en el catalogo. */
    const categories = useMemo(() => {
        const names = new Set<string>();
        products.forEach(p => { if (p.nombre_categoria) names.add(p.nombre_categoria); });
        return ['Todos', ...Array.from(names).sort((a, b) => a.localeCompare(b, 'es'))];
    }, [products]);

    const visibleProducts = useMemo(() => {
        const byCategory = category === 'Todos'
            ? products
            : products.filter(p => p.nombre_categoria === category);

        if (search.trim().length < 2) return byCategory;

        return fuzzySearch(byCategory, search.trim(), {
            keys: ['NombreEtiqueta', 'NombresProductos', 'idProductos', 'barcode', 'CodigoBarras'],
            threshold: 0.35,
        });
    }, [products, category, search]);

    /* ----------------------------------------------------------------- ticket */

    const addToCart = useCallback((product: Product) => {
        setCart(prev => {
            const idx = prev.findIndex(line => line.key === String(product.idProductos));
            if (idx >= 0) {
                return prev.map((line, i) => (i === idx ? { ...line, quantity: line.quantity + 1 } : line));
            }
            return [...prev, { key: String(product.idProductos), product, quantity: 1 }];
        });
        notify('ok', `Agregado: ${nombreOperativo(product)}`);
        searchRef.current?.focus();
    }, [notify]);

    const changeQty = (key: string, delta: number) => {
        setCart(prev => prev.flatMap(line => {
            if (line.key !== key) return [line];
            const qty = line.quantity + delta;
            return qty < 1 ? [] : [{ ...line, quantity: qty }];
        }));
    };

    const removeLine = (key: string) => setCart(prev => prev.filter(line => line.key !== key));

    /**
     * Enter en el buscador: se resuelve primero por coincidencia exacta de codigo
     * (lector de barras) y recien despues por el primer resultado de la busqueda.
     */
    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        const term = search.trim().toLowerCase();
        if (!term) return;

        const exact = products.find(p =>
            String(p.idProductos) === term
            || (p.barcode || '').toLowerCase() === term
            || String(p.CodigoBarras ?? '').toLowerCase() === term
        );
        const target = exact ?? visibleProducts[0];
        if (target) {
            addToCart(target);
            setSearch('');
        } else {
            notify('error', 'Sin resultados para ese código');
        }
    };

    const subtotal = useMemo(
        () => cart.reduce(
            (acc, line) => acc + precioDeLista(line.product.Precio, priceList) * line.quantity,
            0,
        ),
        [cart, priceList],
    );
    const discount = useMemo(
        () => Math.round(subtotal * (Math.min(Math.max(manualDiscount, 0), 100) / 100)),
        [subtotal, manualDiscount],
    );
    // El mostrador no tiene envio: el total es el neto de la venta.
    const total = Math.max(subtotal - discount, 0);

    const resetSale = () => {
        setCart([]);
        setCustomer(null);
        // La lista vuelve a minorista: dejarla en mayorista haria que la venta
        // siguiente saliera con descuento sin que el operador lo note.
        setPriceList('minorista');
        setPaymentMethod('');
        setManualDiscount(0);
        setSelectedPromo('');
        setCoupon('');
        setObservations('');
        setIsMixedPayment(false);
        setMixedPayments([{ metodo: '', monto: 0 }, { metodo: '', monto: 0 }]);
        setCustDni('');
        setCustName('');
        setCustPhone('');
        setCustAddress('');
    };

    /** Guarda la venta en espera y deja el mostrador libre para la siguiente. */
    const suspendSale = () => {
        if (cart.length === 0) return;
        setSuspended(prev => [...prev, cart]);
        resetSale();
        notify('ok', 'Venta suspendida');
    };

    /** Retoma la ultima venta suspendida (descarta lo que haya en el ticket vacio). */
    const resumeSale = () => {
        setSuspended(prev => {
            if (prev.length === 0) return prev;
            setCart(prev[prev.length - 1]);
            return prev.slice(0, -1);
        });
    };

    const reprintLast = () => {
        if (!lastOrder) {
            notify('error', 'No hay ticket para re-imprimir');
            return;
        }
        generateOrderPDF(lastOrder);
    };

    /** Vuelca un cliente fiel de la base al formulario del modal. */
    const aplicarCliente = (found: FamilyMember) => {
        setCustomer(found);
        setCustDni(found.dni || '');
        setCustName(`${found.nombre} ${found.apellido}`.trim());
        setCustPhone(found.telefono || '');
        setCustAddress(found.direccion || '');
    };

    /* ------------------------------------------------------------------ cobro */

    const validMixedPayments = useMemo(
        () => mixedPayments.filter(p => p.metodo && p.monto > 0),
        [mixedPayments],
    );

    /**
     * Lineas del ticket que superan el stock del local.
     *
     * Solo cuentan los productos de los que hay dato de stock: si el reporte no
     * los trajo, no se sabe nada de ellos y avisar seria una falsa alarma. Las
     * promociones no tienen stock propio (lo tienen sus componentes).
     */
    const sinStock = useMemo(
        () => cart.filter(line => {
            const disponible = stockByProduct[line.product.idProductos];
            return !line.product.es_promocion
                && disponible !== undefined
                && disponible < line.quantity;
        }),
        [cart, stockByProduct],
    );

    const checkout = async () => {
        if (cart.length === 0) return;

        if (isMixedPayment) {
            if (validMixedPayments.length < 2) {
                notify('error', 'Pago mixto: completá al menos 2 métodos con monto');
                return;
            }
            const suma = validMixedPayments.reduce((acc, p) => acc + p.monto, 0);
            if (Math.abs(suma - total) > 0.01) {
                notify('error', `Pago mixto: ${money(suma)} no coincide con el total ${money(total)}`);
                return;
            }
        } else if (!paymentMethod) {
            notify('error', 'Elegí un medio de pago');
            return;
        }

        // Los datos del cliente son opcionales: si no se cargaron, la venta sale
        // como consumidor final y se factura despues desde Facturacion.

        // El stock faltante se avisa pero no frena el mostrador: el operador
        // decide en el modal y desde ahi se retoma el cobro.
        if (sinStock.length > 0) {
            setStockWarningOpen(true);
            return;
        }

        await confirmarCobro();
    };

    /** Cobro propiamente dicho, ya resuelta la advertencia de stock. */
    const confirmarCobro = async () => {
        setStockWarningOpen(false);
        setLoading(true);
        const now = new Date();
        // El mostrador entrega en el acto: la fecha y la hora son las de ahora.
        const fecha = todayISO();
        const hora = now.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });

        const observacionesFinales = [coupon ? `Cupón: ${coupon}` : '', observations]
            .filter(Boolean)
            .join(' | ');

        const payload = {
            // Se manda el precio de la lista activa; el backend igual lo recalcula
            // sobre el precio de la base antes de guardar.
            cartItems: cart.map(line => ({
                ...line.product,
                Precio: precioDeLista(line.product.Precio, priceList),
                quantity: line.quantity,
            })),
            listaPrecios: priceList,
            vendedor: user || '',
            dni: custDni || customer?.dni || null,
            nombre: custName || (customer ? `${customer.nombre} ${customer.apellido}` : 'Cliente Mostrador'),
            email: customer?.email || '',
            telefono: custPhone || customer?.telefono || '',
            direccion: custAddress || customer?.direccion || '',
            metodoPago: isMixedPayment ? 'Mixto' : paymentMethod,
            pagosMixtos: isMixedPayment ? validMixedPayments : null,
            pagado: 'Si',
            montoTotal: total,
            // El mostrador siempre cobra en el acto y entrega en mano.
            tipoPedido: 'Inmediato',
            envioDomicilio: 'No',
            costoEnvio: 0,
            observaciones: observacionesFinales,
            fechaEntrega: fecha,
            horarioEntrega: hora,
            restriccionHorario: '',
            medio: 'Local',
            // El local no se elige en pantalla: siempre es el del mostrador.
            id_local: localId,
        };

        try {
            const res = await fetch(`${API}/orders`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                notify('error', 'No se pudo registrar la venta');
                return;
            }
            const result = await res.json();

            const metodoPagoDisplay = isMixedPayment
                ? 'Mixto: ' + validMixedPayments.map(p => `${p.metodo} ${money(p.monto)}`).join(' + ')
                : paymentMethod;

            const order: Order = {
                ID: String(result.orderId),
                Cliente: payload.nombre,
                DNI: payload.dni || '',
                Items: cart.map(line => ({
                    Producto: nombreOperativo(line.product),
                    Cantidad: line.quantity,
                    Precio: precioDeLista(line.product.Precio, priceList),
                    Descuento: 0,
                })),
                // El total lo fija el backend (recalcula precios por lista); si no
                // viniera, se usa el calculado en pantalla.
                Total: typeof result.montoTotal === 'number' ? result.montoTotal : total,
                Fecha: now.toISOString(),
                Pagado: payload.pagado,
                MetodoPago: metodoPagoDisplay,
                Envio: payload.envioDomicilio,
                TipoPedido: payload.tipoPedido,
                Direccion: payload.direccion,
                Telefono: payload.telefono,
                Email: payload.email,
                FechaEntrega: fecha,
                HorarioEntrega: hora,
                RestriccionHorario: payload.restriccionHorario,
                Contacto: payload.telefono || payload.email,
                Observaciones: payload.observaciones,
                EstadoEnvio: 'Pendiente',
                TotalDescuento: discount,
                CostoEnvio: 0,
                pagos_mixtos: isMixedPayment ? validMixedPayments : undefined,
            };

            generateOrderPDF(order);
            setLastOrder(order);
            resetSale();
            notify('ok', 'Venta registrada');
            onSaleCompleted?.();
        } catch (err) {
            console.error('Error cobrando', err);
            notify('error', 'Error de conexión');
        } finally {
            setLoading(false);
        }
    };

    /* --------------------------------------------------------------- rendered */

    const promos = useMemo(() => products.filter(p => p.es_promocion), [products]);

    const activePriceListLabel = PRICE_LISTS.find(l => l.value === priceList)?.label ?? 'Minorista';

    return (
        <div className={styles.pos}>
            <div className={styles.head}>
                <div>
                    <h2 className={styles.headTitle}>POS</h2>
                    <p className={styles.headSub}>Auditoría de operaciones comerciales y reembolsos</p>
                </div>

                {/* La leyenda del descuento va DEBAJO y con altura reservada, para
                    que aparecer y desaparecer no corra el selector de lugar. */}
                <div className={styles.priceList}>
                    <div className={styles.priceListRow}>
                        <span>Lista de Precios</span>
                        <select
                            value={priceList}
                            onChange={e => setPriceList(e.target.value as PriceList)}
                            aria-label="Lista de precios"
                        >
                            {PRICE_LISTS.map(l => (
                                <option key={l.value} value={l.value}>{l.label}</option>
                            ))}
                        </select>
                    </div>
                    <small className={styles.priceListHint}>
                        {priceList === 'mayorista'
                            ? `−${PRICE_LIST_DIFF_PCT}% sobre el precio de lista`
                            : ' '}
                    </small>
                </div>
            </div>

            <div className={styles.tabs} role="tablist">
                {([['ventas', 'Ventas'], ['devoluciones', 'Devoluciones'], ['cambios', 'Cambios']] as [PosTab, string][])
                    .map(([key, label]) => (
                        <button
                            key={key}
                            type="button"
                            role="tab"
                            aria-selected={tab === key}
                            className={`${styles.tab} ${tab === key ? styles.tabActive : ''}`}
                            onClick={() => setTab(key)}
                        >
                            {label}
                        </button>
                    ))}
            </div>

            {tab !== 'ventas' ? (
                <div className={styles.placeholder}>
                    <h3>{tab === 'devoluciones' ? 'Devoluciones' : 'Cambios'}</h3>
                    <p>
                        {tab === 'devoluciones'
                            ? 'Búsqueda del comprobante original y emisión de la nota de crédito.'
                            : 'Cambio de artículos con ajuste de diferencia sobre el comprobante original.'}
                    </p>
                </div>
            ) : (
                <>
                {/* El mostrador es siempre venta inmediata: no hay tipo de
                    operacion, ni sucursal, ni datos de entrega. Los datos del
                    cliente se cargan aparte, desde "Asignar cliente". */}
                <div className={styles.counterBar}>
                    <div className={styles.counterCustomer}>
                        <FaUserPlus className={styles.counterIcon} />
                        <div>
                            <strong>
                                {custName || 'Consumidor Final'}
                            </strong>
                            <small>
                                {custDni ? `Doc. ${custDni}` : 'Sin datos de facturación'}
                            </small>
                        </div>
                        <button
                            type="button"
                            className={styles.counterBtn}
                            onClick={() => setCustomerModalOpen(true)}
                        >
                            {custName ? 'Editar cliente' : 'Asignar cliente'}
                        </button>
                        {custName && (
                            <button
                                type="button"
                                className={styles.counterBtnGhost}
                                onClick={() => {
                                    setCustomer(null);
                                    setCustDni(''); setCustName('');
                                    setCustPhone(''); setCustAddress('');
                                }}
                            >
                                Quitar
                            </button>
                        )}
                    </div>

                    <div className={styles.counterObs}>
                        <label htmlFor="pos-obs">Observaciones</label>
                        <input
                            id="pos-obs"
                            type="text"
                            value={observations}
                            onChange={e => setObservations(e.target.value)}
                            placeholder="Notas de la venta…"
                        />
                    </div>
                </div>

                <div className={styles.body}>
                    {/* ------------------------------------------------ catalogo */}
                    <div className={styles.catalog}>
                        <div className={styles.searchWrap}>
                            <FaSearch className={styles.searchIcon} />
                            <input
                                ref={searchRef}
                                type="text"
                                inputMode="search"
                                autoComplete="off"
                                className={styles.searchInput}
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                onKeyDown={handleSearchKeyDown}
                                placeholder="Escribir nombre, SKU o escanear código de barras..."
                                aria-label="Buscar producto"
                            />
                        </div>

                        <div className={styles.chips}>
                            {categories.map(name => (
                                <button
                                    key={name}
                                    type="button"
                                    className={`${styles.chip} ${category === name ? styles.chipActive : ''}`}
                                    onClick={() => setCategory(name)}
                                >
                                    {name}
                                </button>
                            ))}
                        </div>

                        {visibleProducts.length === 0 ? (
                            <div className={styles.gridEmpty}>
                                No hay productos que coincidan con la búsqueda.
                            </div>
                        ) : (
                            <div className={styles.grid}>
                                {visibleProducts.map(p => {
                                    const stock = stockByProduct[p.idProductos];
                                    const agotado = !p.es_promocion && stock !== undefined && stock <= 0;
                                    const nombre = nombreOperativo(p);
                                    return (
                                        <article key={p.idProductos} className={styles.card}>
                                            <div className={styles.cardMedia}>
                                                {p.UrlImagen ? (
                                                    <img src={p.UrlImagen} alt={nombre} loading="lazy" />
                                                ) : (
                                                    <div className={styles.cardMediaEmpty}><FaBoxOpen /></div>
                                                )}
                                                {!!p.es_promocion && <span className={styles.cardPromo}>Promo</span>}
                                                {agotado && <span className={styles.cardNoStock}>Sin stock</span>}
                                            </div>
                                            <div className={styles.cardBody}>
                                                <span className={styles.cardName}>{nombre}</span>
                                                <span className={styles.cardSku}>
                                                    {p.barcode || p.CodigoBarras || `#${p.idProductos}`}
                                                </span>
                                                <div className={styles.cardFoot}>
                                                    <span className={styles.cardPrice}>
                                                        {money(precioDeLista(p.Precio, priceList))}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        className={styles.cardAdd}
                                                        onClick={() => addToCart(p)}
                                                        aria-label={`Agregar ${nombre}`}
                                                    >
                                                        <FaPlus />
                                                    </button>
                                                </div>
                                            </div>
                                        </article>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* -------------------------------------------------- ticket */}
                    <aside className={styles.ticket}>
                        <div className={styles.ticketHead}>
                            <div className={styles.ticketList}>
                                Lista {activePriceListLabel}
                            </div>
                            <div className={styles.ticketHeadRow}>
                                <strong>Venta Actual</strong>
                                <button
                                    type="button"
                                    className={styles.linkBtn}
                                    onClick={() => setCustomerModalOpen(true)}
                                >
                                    <FaUserPlus />
                                    {custName || 'Asignar Cliente'}
                                </button>
                            </div>
                        </div>

                        <div className={styles.ticketBody}>
                            {cart.length === 0 ? (
                                <p className={styles.ticketEmpty}>
                                    Escaneá o elegí productos para iniciar la venta.
                                </p>
                            ) : cart.map(line => (
                                <div key={line.key} className={styles.line}>
                                    <span className={styles.lineName}>{nombreOperativo(line.product)}</span>
                                    <span className={styles.lineTotal}>
                                        {money(precioDeLista(line.product.Precio, priceList) * line.quantity)}
                                    </span>
                                    <button
                                        type="button"
                                        className={styles.lineRemove}
                                        onClick={() => removeLine(line.key)}
                                        aria-label={`Quitar ${nombreOperativo(line.product)}`}
                                    >
                                        <FaTrashAlt />
                                    </button>
                                    <div className={styles.lineQty}>
                                        <button
                                            type="button"
                                            className={styles.qtyBtn}
                                            onClick={() => changeQty(line.key, -1)}
                                            aria-label="Restar unidad"
                                        >−</button>
                                        <span>
                                            {line.quantity} x {money(precioDeLista(line.product.Precio, priceList))}
                                        </span>
                                        <button
                                            type="button"
                                            className={styles.qtyBtn}
                                            onClick={() => changeQty(line.key, 1)}
                                            aria-label="Sumar unidad"
                                        >+</button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className={styles.adjust}>
                            <div className={styles.adjustRow}>
                                <select
                                    value={selectedPromo}
                                    onChange={e => {
                                        const id = e.target.value;
                                        setSelectedPromo(id);
                                        const promo = promos.find(p => String(p.idProductos) === id);
                                        if (promo) addToCart(promo);
                                    }}
                                    aria-label="Promoción o combo"
                                >
                                    <option value="">Promoción/Combo</option>
                                    {promos.map(p => (
                                        <option key={p.idProductos} value={p.idProductos}>
                                            {nombreOperativo(p)}
                                        </option>
                                    ))}
                                </select>
                                <input
                                    type="text"
                                    value={coupon}
                                    onChange={e => setCoupon(e.target.value)}
                                    placeholder="Cupón"
                                    aria-label="Cupón"
                                />
                            </div>
                            <div className={styles.adjustManual}>
                                <label htmlFor="pos-descuento">
                                    <FaTag /> Descuento Manual (%)
                                </label>
                                <input
                                    id="pos-descuento"
                                    type="number"
                                    inputMode="decimal"
                                    min={0}
                                    max={100}
                                    value={manualDiscount || ''}
                                    onChange={e => setManualDiscount(Number(e.target.value) || 0)}
                                    placeholder="0%"
                                />
                            </div>
                        </div>

                        <div className={styles.totals}>
                            <div className={styles.totalRow}>
                                <span>Subtotal</span>
                                <strong>{money(subtotal)}</strong>
                            </div>
                            <div className={styles.totalRow}>
                                <span>Descuento</span>
                                <strong>{discount > 0 ? `-${money(discount)}` : money(0)}</strong>
                            </div>
                            <div className={styles.totalGrand}>
                                <span>Total</span>
                                <strong>{money(total)}</strong>
                            </div>
                        </div>

                        <div className={styles.pay}>
                            {PAYMENT_METHODS.map(m => (
                                <button
                                    key={m.value}
                                    type="button"
                                    disabled={isMixedPayment}
                                    className={`${styles.payBtn} ${!isMixedPayment && paymentMethod === m.value ? styles.payBtnActive : ''}`}
                                    onClick={() => setPaymentMethod(m.value)}
                                >
                                    {m.icon} {m.label}
                                </button>
                            ))}
                            <button
                                type="button"
                                className={`${styles.payBtn} ${styles.payWide} ${isMixedPayment ? styles.payBtnActive : ''}`}
                                onClick={() => {
                                    setIsMixedPayment(prev => {
                                        if (prev) {
                                            setMixedPayments([{ metodo: '', monto: 0 }, { metodo: '', monto: 0 }]);
                                        } else {
                                            setPaymentMethod('');
                                        }
                                        return !prev;
                                    });
                                }}
                            >
                                <FaCodeBranch /> Dividir Pago
                            </button>
                        </div>

                        {isMixedPayment && (
                            <div className={styles.mixed}>
                                {mixedPayments.map((mp, idx) => (
                                    <div key={idx} className={styles.mixedRow}>
                                        <select
                                            value={mp.metodo}
                                            aria-label={`Método de pago ${idx + 1}`}
                                            onChange={e => setMixedPayments(prev =>
                                                prev.map((p, i) => (i === idx ? { ...p, metodo: e.target.value } : p))
                                            )}
                                        >
                                            <option value="">Método…</option>
                                            {MIXED_PAYMENT_OPTIONS.map(m => <option key={m} value={m}>{m}</option>)}
                                        </select>
                                        <input
                                            type="number"
                                            inputMode="decimal"
                                            min={0}
                                            value={mp.monto || ''}
                                            placeholder="$"
                                            aria-label={`Monto ${idx + 1}`}
                                            onChange={e => setMixedPayments(prev =>
                                                prev.map((p, i) => (i === idx ? { ...p, monto: Number(e.target.value) } : p))
                                            )}
                                        />
                                        {mixedPayments.length > 2 && (
                                            <button
                                                type="button"
                                                className={styles.mixedRemove}
                                                onClick={() => setMixedPayments(prev => prev.filter((_, i) => i !== idx))}
                                                aria-label={`Quitar método ${idx + 1}`}
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                ))}
                                <div className={styles.mixedFoot}>
                                    <button
                                        type="button"
                                        className={styles.mixedAdd}
                                        onClick={() => setMixedPayments(prev => [...prev, { metodo: '', monto: 0 }])}
                                    >
                                        + Método
                                    </button>
                                    {(() => {
                                        const suma = mixedPayments.reduce((acc, p) => acc + (p.monto || 0), 0);
                                        const ok = Math.abs(suma - total) <= 0.01;
                                        return (
                                            <span className={ok ? styles.mixedOk : styles.mixedBad}>
                                                {money(suma)} / {money(total)} {ok ? '✓' : '✗'}
                                            </span>
                                        );
                                    })()}
                                </div>
                            </div>
                        )}

                        <div className={styles.actions}>
                            <button
                                type="button"
                                className={styles.checkout}
                                onClick={checkout}
                                disabled={loading || cart.length === 0}
                            >
                                {loading ? 'Procesando…' : 'Cobrar e Imprimir Ticket'}
                            </button>
                            <button
                                type="button"
                                className={styles.ghostBtn}
                                onClick={cart.length === 0 && suspended.length > 0 ? resumeSale : suspendSale}
                                disabled={cart.length === 0 && suspended.length === 0}
                            >
                                <FaPause /> {cart.length === 0 && suspended.length > 0 ? 'Retomar' : 'Suspender'}
                            </button>
                            <button type="button" className={styles.ghostBtn} onClick={reprintLast} disabled={!lastOrder}>
                                <FaPrint /> Re-imprimir
                            </button>
                        </div>

                        <div className={styles.suspended}>
                            <span className={styles.dot} />
                            Ventas Suspendidas: {suspended.length}
                        </div>
                    </aside>
                </div>
                </>
            )}

            {customerModalOpen && (
                <CustomerModal
                    initial={{ dni: custDni, nombre: custName, telefono: custPhone, direccion: custAddress }}
                    onBuscar={async (q) => {
                        const res = await fetch(`${API}/admin/miembros/search?q=${encodeURIComponent(q)}`);
                        const data = await res.json();
                        return Array.isArray(data) ? (data as FamilyMember[]) : [];
                    }}
                    onSelect={aplicarCliente}
                    onClose={() => setCustomerModalOpen(false)}
                    onSave={(datos) => {
                        setCustDni(datos.dni);
                        setCustName(datos.nombre);
                        setCustPhone(datos.telefono);
                        setCustAddress(datos.direccion);
                        setCustomerModalOpen(false);
                        notify('ok', datos.nombre ? `Cliente: ${datos.nombre}` : 'Datos del cliente actualizados');
                    }}
                />
            )}

            {stockWarningOpen && (
                <div
                    className={styles.overlay}
                    role="presentation"
                    onClick={e => { if (e.target === e.currentTarget) setStockWarningOpen(false); }}
                >
                    <div
                        className={styles.modal}
                        role="alertdialog"
                        aria-modal="true"
                        aria-label="Advertencia de stock"
                    >
                        <div className={styles.modalHead}>
                            <div>
                                <h3>Advertencia de stock</h3>
                                <p>
                                    {sinStock.length === 1
                                        ? 'Un artículo del ticket supera el stock registrado.'
                                        : `${sinStock.length} artículos del ticket superan el stock registrado.`}
                                </p>
                            </div>
                            <button
                                type="button"
                                className={styles.modalClose}
                                onClick={() => setStockWarningOpen(false)}
                                aria-label="Cerrar"
                            >
                                ×
                            </button>
                        </div>

                        <div className={styles.modalBody}>
                            <ul className={styles.stockWarnList}>
                                {sinStock.map(line => (
                                    <li key={line.key}>
                                        <span className={styles.stockWarnName}>
                                            {nombreOperativo(line.product)}
                                        </span>
                                        <span className={styles.stockWarnNums}>
                                            Stock {stockByProduct[line.product.idProductos] ?? 0}
                                            {' · '}
                                            Venta {line.quantity}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                            <p className={styles.stockWarnNote}>
                                Podés cobrar igual: el stock queda en negativo y se corrige
                                con un ajuste.
                            </p>
                        </div>

                        <div className={styles.modalFoot}>
                            <button
                                type="button"
                                className={styles.modalBtn}
                                onClick={() => setStockWarningOpen(false)}
                            >
                                Revisar ticket
                            </button>
                            <button
                                type="button"
                                className={styles.modalBtnPrimary}
                                onClick={confirmarCobro}
                                disabled={loading}
                            >
                                Cobrar igualmente
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {toast && (
                <div className={`${styles.toast} ${toast.type === 'error' ? styles.toastError : ''}`}>
                    {toast.text}
                </div>
            )}
        </div>
    );
};

/* -------------------------------------------------------------- modal cliente */

interface DatosCliente {
    dni: string;
    nombre: string;
    telefono: string;
    direccion: string;
}

interface CustomerModalProps {
    initial: DatosCliente;
    onBuscar: (q: string) => Promise<FamilyMember[]>;
    onSelect: (cliente: FamilyMember) => void;
    onSave: (datos: DatosCliente) => void;
    onClose: () => void;
}

/**
 * Alta/edicion de los datos del cliente de la venta.
 *
 * Todos los campos son opcionales: sirven para poder emitir la factura despues.
 * Se puede buscar un cliente ya cargado o tipear los datos a mano para alguien
 * que compra por unica vez.
 */
const CustomerModal: React.FC<CustomerModalProps> = ({
    initial, onBuscar, onSelect, onSave, onClose,
}) => {
    const [datos, setDatos] = useState<DatosCliente>(initial);
    const [query, setQuery] = useState('');
    const [resultados, setResultados] = useState<FamilyMember[]>([]);
    const [buscando, setBuscando] = useState(false);
    const [sinResultados, setSinResultados] = useState(false);

    const buscar = async () => {
        const q = query.trim();
        if (!q) return;
        setBuscando(true);
        setSinResultados(false);
        try {
            const encontrados = await onBuscar(q);
            setResultados(encontrados);
            setSinResultados(encontrados.length === 0);
        } catch {
            setResultados([]);
            setSinResultados(true);
        } finally {
            setBuscando(false);
        }
    };

    const elegir = (c: FamilyMember) => {
        onSelect(c);
        setDatos({
            dni: c.dni || '',
            nombre: `${c.nombre} ${c.apellido}`.trim(),
            telefono: c.telefono || '',
            direccion: c.direccion || '',
        });
        setResultados([]);
    };

    const set = (campo: keyof DatosCliente) =>
        (e: React.ChangeEvent<HTMLInputElement>) =>
            setDatos(prev => ({ ...prev, [campo]: e.target.value }));

    return (
        <div
            className={styles.overlay}
            role="presentation"
            onClick={e => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className={styles.modal} role="dialog" aria-modal="true" aria-label="Asignar cliente">
                <div className={styles.modalHead}>
                    <div>
                        <h3>Asignar cliente</h3>
                        <p>Opcional. Solo hace falta si la venta se va a facturar.</p>
                    </div>
                    <button type="button" className={styles.modalClose} onClick={onClose} aria-label="Cerrar">
                        ×
                    </button>
                </div>

                <div className={styles.modalBody}>
                    <div className={styles.modalSearch}>
                        <input
                            type="search"
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); buscar(); } }}
                            placeholder="Buscar por DNI, nombre o apellido…"
                            aria-label="Buscar cliente"
                        />
                        <button type="button" className={styles.modalBtn} onClick={buscar} disabled={buscando}>
                            {buscando ? 'Buscando…' : 'Buscar'}
                        </button>
                    </div>

                    {sinResultados && (
                        <p className={styles.modalHint}>
                            No se encontraron clientes. Podés cargar los datos a mano.
                        </p>
                    )}

                    {resultados.length > 0 && (
                        <ul className={styles.modalResults}>
                            {resultados.slice(0, 6).map(c => (
                                <li key={c.dni || `${c.nombre}-${c.apellido}`}>
                                    <button type="button" onClick={() => elegir(c)}>
                                        <strong>{c.nombre} {c.apellido}</strong>
                                        <small>{c.dni ? `DNI ${c.dni}` : 'Sin DNI'}{c.telefono ? ` · ${c.telefono}` : ''}</small>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}

                    <div className={styles.modalGrid}>
                        <label className={styles.modalField}>
                            <span>Nombre y apellido / Razón social</span>
                            <input value={datos.nombre} onChange={set('nombre')} placeholder="Opcional" />
                        </label>
                        <label className={styles.modalField}>
                            <span>CUIT / DNI</span>
                            <input
                                value={datos.dni}
                                onChange={set('dni')}
                                inputMode="numeric"
                                placeholder="Opcional"
                            />
                        </label>
                        <label className={styles.modalField}>
                            <span>Teléfono</span>
                            <input value={datos.telefono} onChange={set('telefono')} placeholder="Opcional" />
                        </label>
                        <label className={styles.modalField}>
                            <span>Domicilio</span>
                            <input value={datos.direccion} onChange={set('direccion')} placeholder="Opcional" />
                        </label>
                    </div>
                </div>

                <div className={styles.modalFoot}>
                    <button
                        type="button"
                        className={styles.ghostBtn}
                        onClick={() => onSave({ dni: '', nombre: '', telefono: '', direccion: '' })}
                    >
                        Vender sin identificar
                    </button>
                    <button type="button" className={styles.payBtn} onClick={() => onSave(datos)}>
                        Guardar datos
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PosScreen;
